import { type DropzoneProps, Dropzone, MIME_TYPES } from '@mantine/dropzone';
import { IconButton, styled, useTheme } from '@mui/material';
import { useMemo, useRef, forwardRef, type ForwardedRef } from 'react';

import { AttachmentOutlinedIcon, DeleteOutlineIcon, UploadFileOutlinedIcon } from '../../icons';
import { Button, Flex, Link, Text } from '../../index';

const Wrapper = styled(Flex)(({ theme }) => ({
  width: '100%',
  padding: theme.spacing(3.5),
  gap: theme.spacing(2),
  alignItems: 'center',
  justifyContent: 'space-between',
}));

const FileItemWrapper = styled(Flex)(({ theme }) => ({
  alignItems: 'center',
  gap: theme.spacing(2),
  justifyContent: 'space-between',
  width: '100%',
}));

const AttachedFileWrapper = styled(Wrapper)(({ theme }) => ({
  backgroundColor: theme.palette.grey[50],
  flexDirection: 'column',
  justifyContent: 'flex-start',
  height: 'max-content',
}));

export interface FileInputProps extends Omit<DropzoneProps, 'children'> {
  buttonText?: string;
  value?: File | File[];
  disabled?: boolean;
  error?: boolean;
  placeholder?: string;
  startIcon?: React.ReactNode;
  onClear?: () => void;
  onRemove?: (files: File[] | null) => void;
  multiple?: boolean;
}

function FileInputBase(props: FileInputProps, ref: ForwardedRef<HTMLDivElement>) {
  const {
    buttonText,
    value,
    error: isError,
    disabled: isDisabled,
    placeholder,
    startIcon,
    multiple: isMultiple = false,
    onRemove,
    ...restProps
  } = props;

  const theme = useTheme();
  const openRef = useRef<() => void>(null);
  const files = useMemo(() => {
    if (Array.isArray(value)) return value;

    return value ? [value] : [];
  }, [value]);

  const handleRemove = (file: File) => () => {
    if (isMultiple) {
      const remainingFiles = files.filter((currentFile) => currentFile !== file);
      onRemove?.(remainingFiles);
      return;
    }
    onRemove?.(null);
  };

  if (files.length > 0) {
    return (
      <AttachedFileWrapper>
        {files.map((file, index) => {
          return (
            <FileItemWrapper key={`${file.name}-${index}`}>
              {startIcon ?? <AttachmentOutlinedIcon sx={{ color: 'text.secondary', ml: 0.5 }} />}
              <Text
                // Just In case we need to open the file on click
                component={Link}
                target='_blank'
                rel='noreferrer'
                sx={{
                  color: 'text.secondary',
                  textDecoration: 'none',
                  width: 1,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitLineClamp: '2',
                  WebkitBoxOrient: 'vertical',
                }}
              >
                {file?.name}
              </Text>
              <IconButton
                onClick={handleRemove(file)}
                size='small'
                sx={{ color: 'text.secondary', p: 0 }}
              >
                <DeleteOutlineIcon />
              </IconButton>
            </FileItemWrapper>
          );
        })}
      </AttachedFileWrapper>
    );
  }

  return (
    <Dropzone
      openRef={openRef}
      disabled={isDisabled}
      sx={{
        width: '100%',
        borderRadius: '4px',
        padding: 0,
        // Note: Consider moving this styling to styled instead
        ...(isDisabled && {
          cursor: 'default',
          backgroundColor: theme.palette.grey[100],
          ':hover': { backgroundColor: theme.palette.grey[100] },
        }),
        borderColor: isError ? theme.palette.error.main : theme.palette.grey[400],
        color: isError ? theme.palette.error.main : theme.palette.text.secondary,
        '&[data-accept]': { color: theme.palette.info.main },
        '&[data-reject]': { color: theme.palette.error.main },
      }}
      multiple={isMultiple}
      {...restProps}
    >
      <Wrapper sx={{ py: 2.5 }} ref={ref}>
        <FileItemWrapper>
          {startIcon ?? (
            <UploadFileOutlinedIcon
              sx={{ ml: 0.5, color: 'text.disabled', width: 24, height: 24 }}
            />
          )}
          <Text sx={{ color: 'text.disabled', width: 1 }}>{placeholder}</Text>
          <Button
            color='secondary'
            variant='outlined'
            onClick={() => openRef.current?.()}
            disabled={isDisabled}
            sx={{ borderRadius: '4px', minWidth: 'fit-content' }}
            size='small'
          >
            {buttonText ?? 'เลือกไฟล์'}
          </Button>
        </FileItemWrapper>
      </Wrapper>
    </Dropzone>
  );
}

const UploadFileInput = forwardRef(FileInputBase);

export default UploadFileInput;

export { MIME_TYPES };
